import { Button } from '@synoptic/ui-kit/button/button.tsx';
import {
  ModalContent,
  ModalHeader,
  ModalRoot,
  ModalTitle,
} from '@synoptic/ui-kit/modal/modal.tsx';
import { useState } from 'react';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import {
  actionsContainer,
  modalDescription,
  docLink,
} from './terms-acceptance-modal.css.ts';
import { useAcceptPrivacyPolicy } from './use-accept-privacy-policy.ts';
import { useAcceptTermsOfService } from './use-accept-terms-of-service.ts';

type TermsAcceptanceModalProps = {
  open: boolean;
  needsTermsAcceptance: boolean;
  needsPrivacyAcceptance: boolean;
  onAccept?: () => void;
};

export const TermsAcceptanceModal = ({
  open,
  needsTermsAcceptance,
  needsPrivacyAcceptance,
  onAccept,
}: TermsAcceptanceModalProps) => {
  const [acceptTerms, { loading: termsLoading }] = useAcceptTermsOfService();
  const [acceptPrivacy, { loading: privacyLoading }] = useAcceptPrivacyPolicy();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loading = termsLoading || privacyLoading || isSubmitting;

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      const promises: Promise<unknown>[] = [];
      if (needsTermsAcceptance) {
        promises.push(acceptTerms());
      }
      if (needsPrivacyAcceptance) {
        promises.push(acceptPrivacy());
      }
      await Promise.all(promises);
      onAccept?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalRoot open={open} onOpenChange={() => {}}>
      <ModalContent preventOutsideClose>
        <ModalHeader withoutCloseIcon>
          <ModalTitle data-testid="terms-acceptance-modal-title">
            Updated Terms and Privacy Policy
          </ModalTitle>
        </ModalHeader>
        <div className={modalDescription}>
          <p>
            We&apos;ve updated our{' '}
            {needsTermsAcceptance && (
              <>
                <Link
                  to={paths.termsOfUse}
                  className={docLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </Link>
              </>
            )}
            {needsTermsAcceptance && needsPrivacyAcceptance && ' and '}
            {needsPrivacyAcceptance && (
              <>
                <Link
                  to={paths.privacyPolicy}
                  className={docLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
              </>
            )}
            . These changes reflect improvements to our services and ensure
            clarity about how we handle your data and your use of our platform.
          </p>
          <p>
            To continue using our platform, please review and agree to the
            updated{' '}
            {needsTermsAcceptance && (
              <>
                <Link
                  to={paths.termsOfUse}
                  className={docLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </Link>
              </>
            )}
            {needsTermsAcceptance && needsPrivacyAcceptance && ' and '}
            {needsPrivacyAcceptance && (
              <>
                <Link
                  to={paths.privacyPolicy}
                  className={docLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
              </>
            )}
            .
          </p>
        </div>
        <div className={actionsContainer}>
          <Button
            onClick={handleAccept}
            loading={loading}
            disabled={loading}
            fullWidth
            data-testid="terms-acceptance-agree-button"
          >
            I Agree
          </Button>
        </div>
      </ModalContent>
    </ModalRoot>
  );
};
